import React, { Suspense, lazy } from "react";
import { Routes, Route, Outlet } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import ParentAuthWrapper from "@/components/layout/ParentAuthWrapper";
import { ParentErrorBoundary } from "@/components/parent-portal";
import { CreateMessageForm } from '@/components/parent-portal/messages/CreateMessageForm';

// Phase 4.1: Lazy loading for mobile-first performance optimization
// Core dashboard loaded immediately for fast initial load
import CleanParentPortalDashboard from "@/pages/ParentPortal/CleanParentPortalDashboard";

// Heavy components lazy loaded for better performance
const ParentPortalStudentDetail = lazy(() => import("@/pages/ParentPortal/ParentPortalStudentDetail"));
const ParentPortalClasses = lazy(() => import("@/pages/ParentPortal/UnifiedParentPortalClasses"));
const ParentPortalStudents = lazy(() => import("@/pages/ParentPortal/UnifiedParentPortalStudents"));
const ParentPortalDocumentsEnhanced = lazy(() => import("@/pages/ParentPortal/ParentPortalDocumentsEnhanced"));
const ParentPortalMessages = lazy(() => import("@/pages/ParentPortal/ParentPortalMessages"));
const ParentPortalApparel = lazy(() => import("@/pages/ParentPortal/ParentPortalApparel"));
const ParentPortalProfile = lazy(() => import("@/pages/ParentPortal/ParentPortalProfile"));
const ParentPoliciesPage = lazy(() => import("@/pages/ParentPortal/ParentPoliciesPage"));
const ParentMakeupClassPage = lazy(() => import("@/pages/ParentPortal/ParentMakeupClassPage"));
const ParentPortalAvailableCourses = lazy(() => import("@/pages/ParentPortal/ParentPortalAvailableCourses"));
const ParentPortalAiNotifications = lazy(() => import("@/pages/ParentPortal/ParentPortalAiNotifications"));
const ParentPortalRegistrationSuccess = lazy(() => import("@/pages/ParentPortal/ParentPortalRegistrationSuccess"));
// ParentPortalUpdates - Future feature, temporarily disabled
const ParentPortalWaitlists = lazy(() => import("@/pages/ParentPortal/ParentPortalWaitlists"));
const ParentPortalEmergencyContacts = lazy(() => import("@/pages/ParentPortal/ParentPortalEmergencyContacts"));
const ParentPortalBilling = lazy(() => import("@/pages/ParentPortal/UnifiedParentPortalBilling"));
const ParentPortalHiveShare = lazy(() => import("@/pages/ParentPortal/ParentPortalHiveShare"));

// Mobile-optimized loading component
const LazyLoadingSpinner = () => (
  <div className="flex justify-center items-center h-64 py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
    <span className="ml-3 text-gray-600">Loading...</span>
  </div>
);

// Wrapper component to use with the element prop
const ParentPortalAuthWrapper = () => {
  return (
    <ParentErrorBoundary>
      <ParentAuthWrapper>
        <Outlet />
      </ParentAuthWrapper>
    </ParentErrorBoundary>
  );
};

const ParentPortalRoutes = () => {
  return (
    <Routes>
      <Route element={<ParentPortalAuthWrapper />}>
        {/* Dashboard loaded immediately for fast initial load */}
        <Route path="/" element={<CleanParentPortalDashboard />} />
        <Route path="/dashboard" element={<CleanParentPortalDashboard />} />
        
        {/* Heavy components wrapped in Suspense for lazy loading */}
        <Route path="/classes" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalClasses />
          </Suspense>
        } />
        <Route path="/students" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalStudents />
          </Suspense>
        } />
        <Route path="/students/:studentId" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalStudentDetail />
          </Suspense>
        } />
        <Route path="/documents" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalDocumentsEnhanced />
          </Suspense>
        } />
        <Route path="/messages" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalMessages />
          </Suspense>
        } />
        <Route path="/messages/new" element={<CreateMessageForm />} />
        <Route path="/messages/:conversationId" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalMessages />
          </Suspense>
        } />
        <Route path="/apparel" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalApparel />
          </Suspense>
        } />
        <Route path="/profile" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalProfile />
          </Suspense>
        } />
        <Route path="/policies" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPoliciesPage />
          </Suspense>
        } />
        <Route path="/makeups" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentMakeupClassPage />
          </Suspense>
        } />
        <Route path="/available-courses" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalAvailableCourses />
          </Suspense>
        } />
        <Route path="/notifications" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalAiNotifications />
          </Suspense>
        } />
        {/* Updates route temporarily disabled - future feature */}
        <Route path="/waitlists" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalWaitlists />
          </Suspense>
        } />
        <Route path="/emergency-contacts" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalEmergencyContacts />
          </Suspense>
        } />
        <Route path="/billing" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalBilling />
          </Suspense>
        } />
        <Route path="/registration-success" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalRegistrationSuccess />
          </Suspense>
        } />
        <Route path="/videos" element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <ParentPortalHiveShare />
          </Suspense>
        } />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default ParentPortalRoutes;
